import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';

/**
 * usePollingData Hook
 * Fetches and manages polling data for a specific store
 * Includes auto-refresh every 30 seconds
 */
export const usePollingData = (storeId, autoRefreshInterval = 30000) => {
  const [pollingData, setPollingData] = useState({
    polls: [],
    selectedPollIndex: 0,
    isLoading: true,
    error: null,
    lastUpdated: null,
    storeName: null
  });

  const intervalRef = useRef(null);

  // Fetch polling data from backend
  const fetchPollingData = useCallback(async () => {
    if (!storeId) {
      setPollingData(prev => ({
        ...prev,
        isLoading: false,
        polls: [],
        error: null
      }));
      return;
    }

    try {
      setPollingData(prev => ({
        ...prev,
        isLoading: true,
        error: null
      }));

      const response = await api.get(`/polls/store/${storeId}/analytics`);

      if (response.data?.success && response.data?.data?.polls) {
        setPollingData({
          polls: response.data.data.polls,
          selectedPollIndex: 0, // Always default to most recent (first)
          isLoading: false,
          error: null,
          lastUpdated: new Date(),
          storeName: response.data.data.store_name || null
        });
      } else {
        setPollingData(prev => ({
          ...prev,
          isLoading: false,
          polls: [],
          error: null,
          storeName: response.data?.data?.store_name || null
        }));
      }
    } catch (error) {
      console.error('Error fetching polling data:', error);
      setPollingData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch polling data'
      }));
    }
  }, [storeId]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (!storeId) {
      return;
    }

    // Initial fetch
    fetchPollingData();

    // Set up interval for auto-refresh
    intervalRef.current = setInterval(() => {
      fetchPollingData();
    }, autoRefreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [storeId, autoRefreshInterval, fetchPollingData]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchPollingData();
  }, [fetchPollingData]);

  // Get current selected poll
  const currentPoll = pollingData.polls[pollingData.selectedPollIndex] || null;

  // Function to change selected poll
  const selectPoll = useCallback((pollIndex) => {
    if (pollIndex >= 0 && pollIndex < pollingData.polls.length) {
      setPollingData(prev => ({
        ...prev,
        selectedPollIndex: pollIndex
      }));
    }
  }, [pollingData.polls.length]);

  // Calculate time since last update
  const getTimeSinceUpdate = useCallback(() => {
    if (!pollingData.lastUpdated) return null;

    const now = new Date();
    const diffMs = now - pollingData.lastUpdated;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);

    if (diffMins === 0) {
      return 'moments ago';
    } else if (diffMins === 1) {
      return '1 minute ago';
    } else if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
  }, [pollingData.lastUpdated]);

  return {
    polls: pollingData.polls,
    currentPoll,
    isLoading: pollingData.isLoading,
    error: pollingData.error,
    lastUpdated: pollingData.lastUpdated,
    timeSinceUpdate: getTimeSinceUpdate(),
    selectedPollIndex: pollingData.selectedPollIndex,
    selectPoll,
    refresh,
    hasMultiplePolls: pollingData.polls.length > 1,
    hasPollData: pollingData.polls.length > 0,
    storeName: pollingData.storeName
  };
};

export default usePollingData;
